import type {
  AreaConfigResponse,
  AreaSelection,
  GeometryType,
  TaxEntry,
  TaxEntryResponse,
  TaxEntryValue,
  TaxScenarioFormData,
  TaxScenarioResponse,
  TaxType,
} from './types';

export type FormMode = 'create' | 'edit';

export const GLOBAL_SELECTION_ID = 'global';

// ─────────────────────────────────────
// Constants
// ─────────────────────────────────────

export const DEFAULT_GEOMETRY_PRIORITY = ['State', 'Geology', 'AOI', 'Unit'];

export const TAX_TYPE_LABELS: Record<TaxType, string> = {
  severance_oil: 'Severance - Oil',
  severance_gas: 'Severance - Gas',
  ad_valorem: 'Ad Valorem',
  conservation: 'Conservation',
  ngl_severance: 'NGL Severance',
  production_other: 'Production/Other',
};

export const TAX_TYPE_NOTES: Record<TaxType, string> = {
  severance_oil: 'TX: 4.6% | OK: 7% | CO: ~2%',
  severance_gas: 'TX: 7.5% | OK: 7% | CO: ~2%',
  ad_valorem: 'Blended county rate for the area',
  conservation: 'NM: 0.19% | TX/OK: 0%',
  ngl_severance: 'State-specific where applicable',
  production_other: 'State-specific where applicable',
};

export const VARIABLE_ELIGIBLE: TaxType[] = ['severance_oil', 'severance_gas'];

export const ALL_TAX_TYPES: TaxType[] = [
  'severance_oil',
  'severance_gas',
  'ad_valorem',
  'conservation',
  'ngl_severance',
  'production_other',
];

// ─────────────────────────────────────
// Defaults
// ─────────────────────────────────────

let nextTempId = -1;

/**
 * Returns a unique negative ID for new client-side entries.
 * @return {number} A negative temporary ID
 */
export function nextId(): number {
  return nextTempId--;
}

/**
 * Creates the default 6 tax entries for a new area.
 * @return {TaxEntry[]} Default tax entries
 */
export function createDefaultTaxEntries(): TaxEntry[] {
  return ALL_TAX_TYPES.map((taxType) => ({
    id: nextId(),
    taxType,
    basis: 'percentage' as const,
    value: { rate: '' },
    notes: '',
  }));
}

export const DEFAULT_AREA_SELECTION: Omit<AreaSelection, 'id'> = {
  status: 'new',
  geometryType: 'area',
  configurableId: null,
  category: '',
  areaName: '',
  unitType: '',
  unitName: '',
  stateName: '',
  alias: '',
  notes: '',
  taxEntries: [],
};

export const DEFAULT_FORM_VALUES: TaxScenarioFormData = {
  global: {
    name: '',
    effectiveDate: '',
    expirationDate: '',
    notes: '',
    geometryPriority: [...DEFAULT_GEOMETRY_PRIORITY],
  },
  areas: [],
};

// ─────────────────────────────────────
// Helpers
// ─────────────────────────────────────

/**
 * Returns the display name for an area.
 * @param {AreaSelection} area - The area selection
 * @return {string} The display name
 */
export function getAreaDisplayName(area: AreaSelection): string {
  return (
    area.alias || area.areaName || area.unitName || area.stateName || 'New Area'
  );
}

/**
 * Returns the nav badge label based on geometry type.
 * @param {AreaSelection} area - The area selection
 * @return {string | undefined} The badge label
 */
export function getAreaBadge(area: AreaSelection): string | undefined {
  if (area.geometryType === 'state') return 'State';
  if (area.geometryType === 'unit') return area.unitType || undefined;
  return area.category || undefined;
}

/**
 * Validates that a string value is non-empty.
 * @param {string} value - The string to validate
 * @return {string | undefined} Error message or undefined if valid
 */
export function validateRequired(value: string): string | undefined {
  return value.trim() ? undefined : 'This field is required';
}

/**
 * Checks whether an area selection has all required fields filled.
 * @param {AreaSelection} area - The area to validate
 * @return {boolean} True if the area geometry selection is complete
 */
export function isAreaSelectionComplete(area: AreaSelection): boolean {
  if (area.geometryType === 'area') return !!area.category && !!area.areaName;
  if (area.geometryType === 'unit') return !!area.unitType && !!area.unitName;
  return !!area.stateName;
}

// ─────────────────────────────────────
// Configurable type mapping
// ─────────────────────────────────────

function toConfigurableType(geometryType: GeometryType): string {
  switch (geometryType) {
    case 'area':
      return 'AreaGeometry';
    case 'unit':
      return 'DevelopmentArea';
    case 'state':
      return 'State';
  }
}

function fromConfigurableType(configurableType: string): GeometryType {
  switch (configurableType) {
    case 'AreaGeometry':
      return 'area';
    case 'DevelopmentArea':
      return 'unit';
    case 'State':
      return 'state';
    default:
      return 'area';
  }
}

// ─────────────────────────────────────
// Payload: Form → Backend (snake_case)
// ─────────────────────────────────────

function serializeValue(entry: TaxEntry): TaxEntryValue {
  if (entry.basis === 'variable' && entry.value.tiers) {
    return {
      tiers: entry.value.tiers.map((t) => ({
        max_price: t.maxPrice,
        rate: t.rate,
      })),
    } as unknown as TaxEntryValue;
  }
  return entry.value;
}

function mapTaxEntryToPayload(e: TaxEntry, originalIds: number[]) {
  const payload: Record<string, unknown> = {
    tax_type: e.taxType,
    basis: e.basis,
    value: serializeValue(e),
    notes: e.notes || null,
  };
  if (originalIds.includes(e.id)) payload.id = e.id;
  return payload;
}

function mapNewAreaToPayload(area: AreaSelection) {
  return {
    configurable_type: toConfigurableType(area.geometryType),
    configurable_id: area.configurableId,
    alias_name: area.alias || null,
    notes: area.notes || null,
    tax_entries_attributes: area.taxEntries.map((e) =>
      mapTaxEntryToPayload(e, [])
    ),
  };
}

/**
 * Builds the full payload for creating a new scenario (POST).
 * @param {TaxScenarioFormData} values - The form values
 * @return {object} The wrapped payload
 */
export function buildCreatePayload(values: TaxScenarioFormData) {
  const { global: g } = values;

  return {
    tax_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      expiration_date: g.expirationDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: values.areas.map(mapNewAreaToPayload),
    },
  };
}

/**
 * Builds a diff payload for updating an existing scenario (PATCH).
 * @param {TaxScenarioFormData} values - Current form values
 * @param {number[]} removedAreaIds - Backend IDs of deleted areas
 * @return {object} The wrapped payload
 */
export function buildUpdatePayload(
  values: TaxScenarioFormData,
  removedAreaIds: number[]
) {
  const { global: g } = values;
  const areaConfigsAttrs: Record<string, unknown>[] = [];

  for (const id of removedAreaIds) {
    areaConfigsAttrs.push({ id, _destroy: true });
  }

  for (const area of values.areas) {
    if (area.status === 'new') {
      areaConfigsAttrs.push(mapNewAreaToPayload(area));
    } else {
      const origIds = area._originalTaxEntryIds ?? [];

      const entries: Record<string, unknown>[] = area.taxEntries.map((e) =>
        mapTaxEntryToPayload(e, origIds)
      );

      const currentIds = new Set(
        area.taxEntries.filter((e) => origIds.includes(e.id)).map((e) => e.id)
      );
      for (const id of origIds) {
        if (!currentIds.has(id)) {
          entries.push({ id, _destroy: true });
        }
      }

      areaConfigsAttrs.push({
        id: area.id,
        configurable_type: toConfigurableType(area.geometryType),
        configurable_id: area.configurableId,
        alias_name: area.alias || null,
        notes: area.notes || null,
        tax_entries_attributes: entries,
      });
    }
  }

  return {
    tax_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      expiration_date: g.expirationDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: areaConfigsAttrs,
    },
  };
}

// ─────────────────────────────────────
// Response mapper: Backend → Form (camelCase)
// ─────────────────────────────────────

function deserializeValue(e: TaxEntryResponse): TaxEntryValue {
  if (e.basis === 'variable' && e.value.tiers) {
    const raw = e.value.tiers as unknown as Array<{
      max_price?: number | null;
      maxPrice?: number | null;
      rate: number | '';
    }>;
    return {
      tiers: raw.map((t) => ({
        clientId: nextId(),
        maxPrice: t.max_price ?? t.maxPrice ?? null,
        rate: t.rate,
      })),
    };
  }
  return e.value;
}

function mapAreaConfigToSelection(ac: AreaConfigResponse): AreaSelection {
  const geometryType = fromConfigurableType(ac.configurableType);
  const isArea = geometryType === 'area';
  const isUnit = geometryType === 'unit';
  const isState = geometryType === 'state';

  return {
    id: ac.id,
    status: 'saved',
    geometryType,
    configurableId: ac.configurableId,
    category: isArea ? (ac.configurableCategory ?? '') : '',
    areaName: isArea ? (ac.configurableDisplayName ?? '') : '',
    unitType: isUnit ? (ac.configurableUnitType ?? '') : '',
    unitName: isUnit ? (ac.configurableDisplayName ?? '') : '',
    stateName: isState ? (ac.configurableDisplayName ?? '') : '',
    alias: ac.aliasName ?? '',
    notes: ac.notes ?? '',
    taxEntries: ac.taxEntries.map((e) => ({
      id: e.id,
      taxType: e.taxType,
      basis: e.basis,
      value: deserializeValue(e),
      notes: e.notes ?? '',
    })),
    _originalTaxEntryIds: ac.taxEntries.map((e) => e.id),
  };
}

/**
 * Converts a backend scenario response into form data and geometries.
 * @param {TaxScenarioResponse} res - The API response
 * @return {{ formData: TaxScenarioFormData, geometries: Record<number, GeoJSON.Geometry> }}
 */
export function mapResponseToFormData(res: TaxScenarioResponse): {
  formData: TaxScenarioFormData;
  geometries: Record<number, GeoJSON.Geometry>;
} {
  const geometries: Record<number, GeoJSON.Geometry> = {};

  const areas = res.areaConfigs.map((ac) => {
    const selection = mapAreaConfigToSelection(ac);
    if (ac.configurableGeojson) {
      const geojson = ac.configurableGeojson;
      geometries[ac.id] =
        'geometry' in geojson && geojson.geometry
          ? geojson.geometry
          : (geojson as unknown as GeoJSON.Geometry);
    }
    return selection;
  });

  return {
    formData: {
      global: {
        name: res.name,
        effectiveDate: res.effectiveDate ?? '',
        expirationDate: res.expirationDate ?? '',
        notes: res.notes ?? '',
        geometryPriority: res.geometryPriority,
      },
      areas,
    },
    geometries,
  };
}
